// Data hook for the Atlas page. Prefers the real GET /brain/map endpoint;
// falls back to the locally generated snapshot (scripts/build-map-data.mjs →
// web/public/map-data.json) so the page works in local dev before the API
// route is deployed.
import { useCallback, useEffect, useState } from "react";
import { getApiUrl, getIdToken } from "./auth";
import type { AtlasData } from "./atlas-types";

export type AtlasSource = "live" | "snapshot";

let cached: { data: AtlasData; source: AtlasSource } | null = null;

function normalize(json: unknown): AtlasData | null {
  const root = json as { atoms?: unknown; map?: { atoms?: unknown } };
  const candidate = Array.isArray(root?.atoms) ? root : root?.map;
  if (!candidate || !Array.isArray((candidate as AtlasData).atoms)) return null;
  const d = candidate as AtlasData;
  return { generatedAt: d.generatedAt ?? 0, atoms: d.atoms, knn: d.knn ?? [], clusters: d.clusters ?? [] };
}

async function fetchAtlas(): Promise<{ data: AtlasData; source: AtlasSource }> {
  try {
    const token = await getIdToken();
    const res = await fetch(`${getApiUrl()}/brain/map`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`brain/map ${res.status}`);
    const data = normalize(await res.json());
    if (!data) throw new Error("brain/map: unexpected shape");
    return { data, source: "live" };
  } catch (err) {
    // The snapshot fallback is DEV-ONLY: the generated file is tenant-unfiltered
    // and must never be served in production (and CloudFront's 404→index.html
    // rewrite would break the fetch there anyway) — surface the real error.
    if (!import.meta.env.DEV) throw err;
    const res = await fetch("/map-data.json");
    if (!res.ok) {
      throw new Error(
        "No atlas data: /brain/map is unavailable and no local snapshot exists. Run scripts/build-map-data.mjs to generate web/public/map-data.json."
      );
    }
    const data = normalize(await res.json());
    if (!data) throw new Error("map-data.json: unexpected shape");
    return { data, source: "snapshot" };
  }
}

export function useAtlasData(): {
  data: AtlasData | null;
  source: AtlasSource | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
} {
  const [state, setState] = useState<{
    data: AtlasData | null;
    source: AtlasSource | null;
    loading: boolean;
    error: string | null;
  }>(() => (cached ? { ...cached, loading: false, error: null } : { data: null, source: null, loading: true, error: null }));

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchAtlas()
      .then((r) => {
        cached = r;
        setState({ ...r, loading: false, error: null });
      })
      .catch((err: unknown) => {
        setState({ data: null, source: null, loading: false, error: err instanceof Error ? err.message : String(err) });
      });
  }, []);

  useEffect(() => {
    if (!cached) load();
  }, [load]);

  return { ...state, reload: load };
}
